import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Axios from 'axios';
import {
  Col,
  Form,
  Row,
  Button,
  Container,
  Modal,
  Carousel,
  Tooltip,
  OverlayTrigger,
} from 'react-bootstrap';
import { PDFViewer } from '@progress/kendo-react-pdf-viewer';
import { FaTh, FaList, FaFile, FaQuestionCircle } from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import Pagination from '@vlsergey/react-bootstrap-pagination';
import { useDebounce } from 'use-debounce'; // Add this import
import AgeCheckComponent from '../age-check/AgeCheckComponent';
import Logo from '../../images/logo-sm.png';
import {
  setSearchFilter,
  setSearchQuery,
  setSearchPage,
  setSearchSort,
  setSearchStatus,
} from '../../state/ducks/search/actions';
import { requestGetItem, requestGetItems, requestGetItemPackage, requestSearchSummary } from '../../state/ducks/items/api';
import ItemNameFilter from '../common/filters/ItemNameFilter';
import ItemCategoryFilter from '../common/filters/ItemCategoryFilter';
import StateFilter from '../common/filters/StateFilter';
import LicenseNumberFilter from '../common/filters/LicenseNumberFilter';
import QuantityTypeFilter from '../common/filters/QuantityTypeFilter';
import PackageIdFilter from '../common/filters/PackageIdFilter';
import './ResultsPage.scss';
import ItemGridResult from '../item-grid-result/ItemGridResult';
import ItemListResult from '../item-list-result/ItemListResult';
import config from '../../common/config';
import SearchBox from '../search-box/SearchBox';

export default () => {
  const qs = new URLSearchParams(window.location.search);
  const tools = ['pager', 'spacer', 'zoomInOut', 'zoom', 'selection', 'spacer', 'search', 'download', 'print'];

  const dispatch = useDispatch();
  const history = useNavigate();
  const query = useSelector((state) => state.search.query);
  const [debouncedQuery] = useDebounce(query, 500);
  const page = useSelector((state) => state.search.page);
  const sort = useSelector((state) => state.search.sort);
  const searchFilters = useSelector((state) => state.search.filters);
  const items = useSelector((state) => state.items);
  const itemDetails = useSelector((state) => state.items.itemDetails);
  const itemPackageDetails = useSelector((state) => state.items.itemPackageDetails);
  const [display, setDisplay] = useState('grid');

  const [showItemModal, setShowItemModal] = useState(false);
  const [showItemPackageModal, setShowItemPackageModal] = useState(false);
  const [stateLoadedFromUrl, setStateLoadedFromUrl] = useState(false);
  const [showRetailLicense, setShowRetailLicense] = useState(false);
  const [showPdf, setShowPdf] = useState(false);

  const ageCheckPassed = useSelector((state) => state.ageCheck.ageCheckAccepted);

  const handleClose = () => { setShowRetailLicense(false); setShowItemModal(false); }
  const handlePackageClose = () => { setShowItemPackageModal(false); }
  const onItemClick = (item) => {
    dispatch(requestGetItem(item.id));
    setShowItemModal(true);
  };
  const onItemPackageClick = (itemPackage) => {
    dispatch(requestGetItemPackage(itemPackage));
    setShowItemPackageModal(true);
    setShowPdf(true);
  };

  const updateHistory = () => {
    const filters = Object.keys(searchFilters).reduce((acc, curr) => {
      const values = (searchFilters[curr] || []).map((option) => option.value);
      if (values.length) {
        acc[curr] = values;
      }
      return acc;
    }, {});

    if (window.location.search.substring(1) === new URLSearchParams({ query, ...filters, sort }).toString()) return

    history({
      pathname: '/results',
      search: new URLSearchParams({ query, ...filters, sort }).toString(),
      replace: true,
    });
  };

  const onFilterChange = (name, values) => {
    dispatch(setSearchFilter(name, values));
    dispatch(setSearchPage(1));
    dispatch(setSearchStatus('running'));
  };

  const onSortChange = (onSort) => {
    dispatch(setSearchSort(onSort.toLowerCase()));
    dispatch(setSearchPage(1));
    dispatch(setSearchStatus('running'));
  };

  const WarningText = () => {
    if (items.searchResults.results && items.searchResults.results.length === 0 ) {
      return "Your query didn't match any items in our catalog.";
    }
    return null;
  }

  if (!stateLoadedFromUrl) {
    const qsStates = qs.get('states');
    const abbreviations = qsStates ? qsStates.split(',') : undefined;

    if (abbreviations && abbreviations.length) {
      // state values in URL are abbreviations.  call server to get full state names as well
      Axios.get(`${config.apiBaseUrl}/states`, {
        params: { values: abbreviations },
      }).then(({ data: states }) => {
        onFilterChange(
          'states',
          states.map(({ abbreviation: value, name: label }) => ({
            label,
            value,
          }))
        );
      });
    }

    // these values are all strings, so label is the same as value
    ['itemCategories', 'licenseNumbers', 'quantityTypes', 'itemNames','packageIds'].forEach(
      (filter) => {
        const qsValue = qs.get(filter);
        const values = qsValue
          ? qsValue.split(',').map((v) => ({ label: v, value: v }))
          : undefined;
        onFilterChange(filter, values);
      }
    );

    const qsSort = qs.get('sort');
    if (qsSort) {
      dispatch(setSearchSort(qsSort.toLowerCase()));
    }

    const qsQuery = qs.get('query');
    if (qsQuery) {
      dispatch(setSearchQuery(qsQuery));
    }

    setStateLoadedFromUrl(true);
  }

  useEffect(() => {
    const filters = Object.keys(searchFilters || []).reduce((prev, cur) => {
      prev[cur] = (searchFilters[cur] || []).map((filter) => filter.value);
      return prev;
    }, {});

    dispatch(requestGetItems(debouncedQuery, page, sort, filters));
    updateHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQuery, page, sort, searchFilters]);

  const renderTooltip = (props) => (
    <Tooltip id="button-tooltip" {...props}>
      Search products using words, otherwise leave this blank and use the filters below or the search box above.
    </Tooltip>
  );

  const paginationChange = (searchPage) => {
    dispatch(setSearchPage(searchPage + 1));
    dispatch(setSearchStatus('running'));
  };

  let start, results, total;

  if (items && items.searchResults && items.searchResults.meta) {
    start = items.searchResults.meta.start + 1;
    results = items.searchResults.meta.results;
    total = items.searchResults.meta.total;
  }

  return (
    <div id="results-page" className="px-4">
      <AgeCheckComponent />
      {ageCheckPassed && <Container fluid>
        <Row className="mt-4">
          <Col className="text-left col-auto">
            <Link to="/">
              <img id="logo" src={Logo} alt="Metrc" />
            </Link>
          </Col>
          <Col>
            <SearchBox className="resultsPageSearchBox" ></SearchBox>         
          </Col>
        </Row>
        <Row>
          <Form className="d-inline-flex">
            <Form.Label className="ms-auto mb-0 pt-2 me-3">Sort by</Form.Label>
            <Form.Select
              className="me-3"
              style={{ width: 'auto' }}
              value={sort}
              onChange={({ target: { value } }) => onSortChange(value)}
            >
              <option value="relevancy">Relevancy</option>
              <option value="name">Name</option>
              <option value="category">Category</option>
            </Form.Select>
            <FaTh
              onClick={() => setDisplay('grid')}
              className={classNames(
                'display-option',
                'me-3',
                'mt-1',
                'd-none',
                'd-xl-block',
                {
                  selected: display === 'grid',
                }
              )}
              style={{ fontSize: 30 }}
            />
            <FaList
              onClick={() => setDisplay('list')}
              className={classNames(
                'display-option',
                'me-3',
                'mt-1',
                'd-none',
                'd-xl-block',
                {
                  selected: display === 'list',
                }
              )}
              style={{ fontSize: 30 }}
            />
          </Form>
        </Row>
        <Row>
          <Col md={display === 'grid' ? 4 : 12} lg={3}>
            <div id="filters">
              <Row>
                <Col>
                  <Form.Group controlId="formQuery">
                    <Form.Label className='mb-0'>Search Name, Category, License:</Form.Label>                    
                    <Form.Text className="text-muted xs mt-0">
                      <OverlayTrigger
                        placement="right"
                        delay={{ show: 250, hide: 400 }}
                        overlay={renderTooltip}>
                        <FaQuestionCircle className='ms-1 mb-2 mt-1'/>
                      </OverlayTrigger>
                    </Form.Text>
                    <Form.Control
                      value={query}
                      onChange={(e) => { 
                        dispatch(setSearchQuery(e.target.value));
                        dispatch(setSearchStatus('running'));
                      }}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col>
                  <Form.Group controlId="formState">
                    <Form.Label>State</Form.Label>
                    <StateFilter
                      value={searchFilters.states}
                      onChange={(values) => onFilterChange('states', values)}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col>
                  <Form.Group controlId="formItemName">
                    <Form.Label>Item Name</Form.Label>
                    <ItemNameFilter
                      value={searchFilters.itemNames}
                      requestQueryParams={{
                        states: (searchFilters.states || []).map(
                          (s) => s.value
                        ),
                      }}
                      onChange={(values) => onFilterChange('itemNames', values)}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col>
                  <Form.Group controlId="formItemCategory">
                    <Form.Label>Item Category</Form.Label>
                    <ItemCategoryFilter
                      value={searchFilters.itemCategories}
                      requestQueryParams={{
                        states: (searchFilters.states || []).map(
                          (s) => s.value
                        ),
                      }}
                      onChange={(values) =>
                        onFilterChange('itemCategories', values)
                      }
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col>
                  <Form.Group controlId="formLicenseNumber">
                    <Form.Label>License Number</Form.Label>
                    <LicenseNumberFilter
                      value={searchFilters.licenseNumbers}
                      requestQueryParams={{
                        states: (searchFilters.states || []).map(
                          (s) => s.value
                        ),
                      }}
                      onChange={(values) =>
                        onFilterChange('licenseNumbers', values)
                      }
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col>
                  <Form.Group controlId="formQuantityType">
                    <Form.Label>Quantity Type</Form.Label>
                    <QuantityTypeFilter
                      value={searchFilters.quantityTypes}
                      requestQueryParams={{
                        states: (searchFilters.states || []).map(
                          (s) => s.value
                        ),
                      }}
                      onChange={(values) =>
                        onFilterChange('quantityTypes', values)
                      }
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col>
                  <Form.Group controlId="formPackageId">
                    <Form.Label>Package ID Number</Form.Label>
                    <PackageIdFilter
                      value={searchFilters.packageIds}
                      requestQueryParams={{
                        states: (searchFilters.states || []).map(
                          (s) => s.value
                        ),
                      }}
                      onChange={(values) =>
                        onFilterChange('packageIds', values)
                      }
                    />
                  </Form.Group>
                </Col>
              </Row>
            </div>
          </Col>
          <Col md={display === 'grid' ? 8 : 12} lg={9}>
            <Row className="pt-3">
              {items &&
                items.searchResults.results &&
                items.searchResults.results.map((item) => (
                  <Col
                    xs={12}
                    sm={display === 'grid' ? 6 : 12}
                    lg={display === 'grid' ? 4 : 12}
                    xl={display === 'grid' ? 3 : 12}
                    className={classNames('d-flex', 'flex-column', {
                      'col-xxl-2': display === 'grid',
                    })}
                    key={item.id}
                  >
                    {display === 'grid' ? (
                      <ItemGridResult
                        className="h-100"
                        onClick={() => onItemClick(item)}
                        {...item}
                      />
                    ) : (
                      <ItemListResult
                        className="h-100"
                        onClick={() => onItemClick(item)}
                        {...item}
                      />
                    )}
                  </Col>
                ))}
            </Row>
            <Row className="mt-3">
              {total ? (
                <Col className="mb-3">
                  {start.toLocaleString()}-{(start + results - 1).toLocaleString()} of {total.toLocaleString()} results
                </Col>
              ) : (
                <Col className="mb-3">
                  <span>
                    <WarningText />
                  </span>
                </Col>
              )}
              <Col className="col-auto">
                {!!total && (
                  <Pagination
                    value={page - 1}
                    totalPages={Math.ceil(total / 24)}
                    onChange={({ target: { value: paginationPage } }) =>
                      paginationChange(paginationPage)
                    }
                  />
                )}
              </Col>
            </Row>
          </Col>
        </Row>
      </Container>
      }
      {showItemModal && itemDetails && (
        <Modal show={showItemModal} onHide={handleClose} size="xl">
          <Modal.Header closeButton>
            <Modal.Title className="px-3">{itemDetails.name}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Container>
              <Row>
                <Col xs={12} lg={6}>
                  <Carousel indicators={false} className="mb-3">
                    {itemDetails.images.map((img) => (
                      <Carousel.Item key="{img.url}">
                        <img
                          className="d-block w-100 carousel-image"
                          src={`${config.imageServerUrl}${img.url}`}
                          alt='product'
                        />
                      </Carousel.Item>
                    ))}
                  </Carousel>
                </Col>
                <Col xs={12} lg={6} className="retailAvailability contents">
                  <div className="content">
                    <div className="d-flex">
                      <div className="category font-weight-bold">{itemDetails.category}</div>
                      <div className="state ms-auto">{itemDetails.state}</div>
                    </div>
                    <div className="row">
                      <div className="col-6 col-xl-4">
                        {itemDetails.thcPotency && `THC: ${itemDetails.thcPotency}%`}
                      </div>
                      <div className="col-6 col-xl-4">
                        {itemDetails.cbdPotency && `CBD: ${itemDetails.cbdPotency}%`}
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-6 col-xl-4">
                        {itemDetails.thcContent && `THC: ${itemDetails.thcContent}`}
                        {itemDetails.thcContentUnitOfMeasure && `${itemDetails.thcContentUnitOfMeasure}`}{itemDetails.thcContent && <br />}
                      </div>
                      <div className="col-6 col-xl-4">
                        {itemDetails.cbdContent && `CBD: ${itemDetails.cbdContent}`}
                        {itemDetails.cbdContentUnitOfMeasure && `${itemDetails.cbdContentUnitOfMeasure}`}{itemDetails.cbdContent && <br />}
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-6 col-xl-4">
                        {itemDetails.thcContentDose && `THC Dose: ${itemDetails.thcContentDose}`}
                        {itemDetails.thcContentDoseUnitOfMeasure && `${itemDetails.thcContentDoseUnitOfMeasure}`}{itemDetails.thcContentDose && <br />}
                      </div>                    
                      <div className="col-6 col-xl-4">
                        {itemDetails.cbdContentDose && `CBD Dose: ${itemDetails.cbdContentDose}`}
                        {itemDetails.cbdContentDoseUnitOfMeasure && `${itemDetails.cbdContentDoseUnitOfMeasure}`}{itemDetails.cbdContentDose && <br />}
                      </div>
                    </div>
                    <div className="license">{itemDetails.facilityName}</div>
                    <div className="d-flex">
                      <div className="license">{itemDetails.license}</div>&nbsp;&nbsp;
                      <div>
                        {itemDetails.retailAvailability.length > 0 &&
                          <a
                            role="button"
                            tabIndex={0}
                            onClick={() => { setShowRetailLicense(!showRetailLicense); return false; }}
                            onKeyDown={() => { setShowRetailLicense(!showRetailLicense); return false; }}>
                              View {showRetailLicense ? 'less' : 'more'}
                          </a>
                        }
                      </div>
                    </div>
                    {showRetailLicense &&
                      <div>
                        <div>
                          {(itemDetails.retailAvailability.map((i) => (
                            <div className="ms-4 d-flex">
                              <div>&#8226;&nbsp;</div>
                              <div className="w-75">{i.split('|')[0]}</div>
                              <div className="text-right w-25">{i.split('|')[1]}</div>
                            </div>
                          )))}
                        </div>
                      </div>
                    }
                    <div className="mt-3">
                      <h3>Description</h3>
                      {itemDetails.description ||
                        'No description available for this item'}
                    </div>
                    {itemDetails.requiresAllergens && (
                      <div className="mt-3">
                        <h3>Allergens</h3>
                        {itemDetails.allergens ||
                          'No allergens available for this item'}
                      </div>
                    )}
                    {!!itemDetails.ingredients.length && (
                      <div className="mt-3">
                        <h3>Ingredients</h3>
                        {itemDetails.ingredients.join(', ')}
                      </div>
                    )}
                    {!!itemDetails.packages.length && (
                      <div className="mt-3">
                        <h3>Packages</h3>
                        {(itemDetails.packages.map((itemPackage) => (
                          <div className="ms-4 d-flex" key={itemPackage.electronicProductCode}>
                            <div>&#8226;&nbsp;</div>
                            <a
                              role="button"
                              tabIndex={0}
                              className="w-75"
                              onClick={() => onItemPackageClick(itemPackage)}
                              onKeyDown={() => onItemPackageClick(itemPackage)}>
                              {itemPackage.electronicProductCode} {itemPackage.authenticityCertificateImage && 
                                <FaFile />}
                            </a>
                          </div>
                        )))}
                      </div>
                    )}
                  </div>
                </Col>
              </Row>
            </Container>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleClose}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>
      )}
      {showItemPackageModal && itemPackageDetails && (
        <Modal show={showItemPackageModal} onHide={handlePackageClose} size="xl">
          <Modal.Header closeButton>
            <Modal.Title className="px-3">{itemDetails.name}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Container>
              <Row>
                <Col xs={12} lg={3} className="packageDetails contents">
                  <div className="content">
                    <div className="row">
                      <div>EPC: {itemPackageDetails.electronicProductCode}</div>
                    </div>
                  </div>
                </Col>
              </Row>
              {itemPackageDetails.authenticityCertificateImage && showPdf && (
                (
                  <div>
                    <h3 className="text-center" >Certificate Of Analysis</h3>
                    <PDFViewer url={`${config.documentServerUrl}${itemPackageDetails.authenticityCertificateImage}`} style={{ height: 615, width: '100%' }} 
                      tools={tools} defaultZoom={1}
                      saveFileName={'COA_' + itemPackageDetails.electronicProductCode}/>
                  </div>
                )
              )}
            </Container>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handlePackageClose}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </div>
  );
};
